import React, { useEffect, useState } from "react";
import { Menu } from "@headlessui/react";
import { HiOutlinePlus } from "react-icons/hi";
import { toast } from "react-hot-toast";
import LinksComponent from "../../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../../components/Tenant/DataTableComponent/DataTableComponent";
import ModalComponent from "../../../../components/Tenant/ModalComponent/ModalComponent";
import { CircleLoaderComponent } from "../../../../components/Loader/CircleLoaderComponent";
import {
  useFetchDistrictMasterStatesMutation,
  useFetchDistrictMasterListMutation,
} from "../../../../features/tenant/masters/zoneStateMasterApi";
import {
  useAddCityMasterMutation,
  useDeleteCityMasterMutation,
  useFetchCityMasterListMutation,
  useUpdateCityMasterMutation,
  useUploadCityMasterCsvMutation,
} from "../../../../features/tenant/masters/cityMasterApi";
import { downloadSampleCSV } from "../../../../utils/downloadSampleCsv";
import { parseCSV } from "../../../../utils/parseCsv";
import { downloadJsonAsCsv } from "../../../../utils/downloadJsonAsCsv";

const CITY_NAME_MAX_LENGTH = 255;
const initialFilters = { city: "", state_id: "", district_id: "" };
const initialForm = { id: null, city: "", state_id: "", district_id: "", location_count: 0 };
const cityCsvTemplate = [
  { name: "state", label: "Sample State", maxLength: 255 },
  { name: "district", label: "Sample District", maxLength: 255 },
  { name: "city", label: "Sample City", maxLength: CITY_NAME_MAX_LENGTH },
];

const inputClass =
  "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500";

const CityMasterList = ({ embedded = false }) => {
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);
  const [cityList, setCityList] = useState([]);
  const [total, setTotal] = useState(0);
  const [states, setStates] = useState([]);
  const [filterDistricts, setFilterDistricts] = useState([]);
  const [formDistricts, setFormDistricts] = useState([]);
  const [formDistrictsLoading, setFormDistrictsLoading] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addTab, setAddTab] = useState("manual");
  const [csvFile, setCsvFile] = useState(null);

  const [fetchStates, { isLoading: statesLoading }] = useFetchDistrictMasterStatesMutation();
  const [fetchDistrictsForFilter, { isLoading: filterDistrictsLoading }] = useFetchDistrictMasterListMutation();
  const [fetchDistrictsForForm] = useFetchDistrictMasterListMutation();
  const [fetchCityList, { isLoading: listLoading }] = useFetchCityMasterListMutation();
  const [addCity, { isLoading: addLoading }] = useAddCityMasterMutation();
  const [updateCity, { isLoading: updateLoading }] = useUpdateCityMasterMutation();
  const [deleteCity, { isLoading: deleteLoading }] = useDeleteCityMasterMutation();
  const [uploadCityCsv, { isLoading: csvUploading }] = useUploadCityMasterCsvMutation();
  const [fetchCityListForExport, { isLoading: exportLoading }] = useFetchCityMasterListMutation();

  const isEdit = Boolean(form.id);
  const isSaving = addLoading || updateLoading;

  const authParams = () => ({
    token: sessionStorage.getItem("access_token"),
    tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
  });

  const loadCities = async (appliedFilters = filters, pageLimit = limit, pageOffset = offset) => {
    try {
      const res = await fetchCityList({
        ...authParams(),
        body: { ...appliedFilters, limit: pageLimit, offset: pageOffset },
      }).unwrap();
      setCityList(res?.body?.rows || []);
      setTotal(res?.body?.total || 0);
    } catch (error) {
      toast.error(error?.data?.message || "Error fetching city list");
    }
  };

  useEffect(() => {
    fetchStates(authParams())
      .unwrap()
      .then((res) => setStates(Array.isArray(res?.body) ? res.body : []))
      .catch((error) => toast.error(error?.data?.message || "Error fetching states"));
  }, []);

  useEffect(() => {
    loadCities();
  }, [limit, offset]);

  const handlePageChange = (pageNumber) => {
    setOffset((pageNumber - 1) * limit);
    setCurrentPage(pageNumber);
  };

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setOffset(0);
    setCurrentPage(1);
  };

  const applyFilters = (appliedFilters) => {
    setCurrentPage(1);
    if (offset === 0) {
      loadCities(appliedFilters, limit, 0);
    } else {
      setOffset(0);
    }
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setFilterDistricts([]);
    applyFilters(initialFilters);
  };

  const handleFilterStateChange = (stateId) => {
    const next = { ...filters, state_id: stateId, district_id: "" };
    setFilters(next);
    setFilterDistricts([]);

    if (!stateId) return;

    fetchDistrictsForFilter({ ...authParams(), body: { state_id: stateId, limit: 1000, offset: 0 } })
      .unwrap()
      .then((res) => setFilterDistricts(res?.body?.rows || []))
      .catch((error) => toast.error(error?.data?.message || "Error fetching districts"));
  };

  const loadFormDistricts = async (stateId) => {
    setFormDistricts([]);
    if (!stateId) return;

    setFormDistrictsLoading(true);
    try {
      const res = await fetchDistrictsForForm({ ...authParams(), body: { state_id: stateId, limit: 1000, offset: 0 } }).unwrap();
      setFormDistricts(res?.body?.rows || []);
    } catch (error) {
      toast.error(error?.data?.message || "Error fetching districts");
    } finally {
      setFormDistrictsLoading(false);
    }
  };

  const handleFormStateChange = (stateId) => {
    setForm((prev) => ({ ...prev, state_id: stateId, district_id: "" }));
    loadFormDistricts(stateId);
  };

  const openAddModal = () => {
    setForm(initialForm);
    setFormDistricts([]);
    setAddTab("manual");
    setCsvFile(null);
    setShowFormModal(true);
  };

  const openEditModal = async (row) => {
    setForm({
      id: row.id,
      city: row.city || "",
      state_id: "",
      district_id: row.district_id ? String(row.district_id) : "",
      location_count: row.location_count || 0,
    });
    setFormDistricts([]);
    setShowFormModal(true);

    // The city list no longer returns state_id, so resolve the district's state (to preselect it
    // and populate the district dropdown) by fetching the full district list and matching by id.
    if (row.district_id) {
      setFormDistrictsLoading(true);
      try {
        const res = await fetchDistrictsForForm({ ...authParams(), body: { limit: 1000, offset: 0 } }).unwrap();
        const allDistricts = res?.body?.rows || [];
        const matched = allDistricts.find((district) => String(district.id) === String(row.district_id));
        if (matched?.state_id) {
          setForm((prev) => ({ ...prev, state_id: String(matched.state_id) }));
          setFormDistricts(allDistricts.filter((district) => String(district.state_id) === String(matched.state_id)));
        }
      } catch (error) {
        toast.error(error?.data?.message || "Error fetching districts");
      } finally {
        setFormDistrictsLoading(false);
      }
    }
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setForm(initialForm);
    setFormDistricts([]);
    setAddTab("manual");
    setCsvFile(null);
  };

  const handleCsvSubmit = () => {
    if (!csvFile) {
      toast.error("Please select a file");
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = async (event) => {
      const rows = parseCSV(event.target.result);
      try {
        const res = await uploadCityCsv({ ...authParams(), body: { rows } }).unwrap();
        const insertedCount = res?.body?.inserted?.length || 0;
        const districtCount = res?.body?.createdDistricts?.length || 0;
        const skippedCount = res?.body?.skipped?.length || 0;
        toast.success(
          `${insertedCount} city(ies) added${districtCount ? `, ${districtCount} district(s) created` : ""}, ${skippedCount} skipped`
        );
        closeFormModal();
        loadCities();
      } catch (error) {
        toast.error(error?.data?.message || "Error uploading city CSV");
      }
    };
    fileReader.readAsText(csvFile);
  };

  const handleExportCsv = async () => {
    try {
      const res = await fetchCityListForExport({ ...authParams(), body: { limit: 100000, offset: 0 } }).unwrap();
      const rows = res?.body?.rows || [];
      if (rows.length === 0) {
        toast.error("No cities to export");
        return;
      }
      downloadJsonAsCsv(rows, "city_master.csv");
    } catch (error) {
      toast.error(error?.data?.message || "Error exporting city CSV");
    }
  };

  const handleSave = async () => {
    const cityName = form.city.trim();

    if (!cityName) {
      toast.error("City name is required");
      return;
    }

    const body = { city: cityName, district_id: form.district_id ? Number(form.district_id) : undefined };

    try {
      if (isEdit) {
        await updateCity({ ...authParams(), body: { id: form.id, ...body } }).unwrap();
        toast.success("City updated successfully");
      } else {
        await addCity({ ...authParams(), body }).unwrap();
        toast.success("City added successfully");
      }
      closeFormModal();
      loadCities();
    } catch (error) {
      toast.error(error?.data?.message || (isEdit ? "Error updating city" : "Error adding city"), { duration: 6000 });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteCity({ ...authParams(), body: { id: deleteTarget.id } }).unwrap();
      toast.success("City deleted successfully");
      setDeleteTarget(null);

      // If the last row of a later page was deleted, step back one page.
      if (cityList.length === 1 && offset > 0) {
        setOffset(Math.max(0, offset - limit));
        setCurrentPage((page) => Math.max(1, page - 1));
      } else {
        loadCities();
      }
    } catch (error) {
      setDeleteTarget(null);
      toast.error(error?.data?.message || "Error deleting city", { duration: 6000 });
    }
  };

  const columns = [
    { name: "SNo", cell: (row, idx) => offset + Number(idx + 1), width: "80px" },
    { name: "City", selector: (row) => row.city || "-" },
    { name: "Zone Locations", selector: (row) => row.location_count ?? 0 },
    {
      name: "",
      minWidth: "180px",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => openEditModal(row)}
            className="flex justify-center items-center rounded px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition duration-150 ease-in-out"
            style={{ backgroundColor: primaryColor?.["700"] || "#2563eb" }}
          >
            Edit
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="flex justify-center items-center rounded bg-red-600 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition duration-150 ease-in-out hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const isBusy = listLoading || statesLoading || filterDistrictsLoading || isSaving || deleteLoading || exportLoading;

  const content = (
    <div className="p-3">
      <div className="p-3 w-full bg-white shadow rounded-lg">
        <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
          <div className="flex space-x-2 justify-end">
            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">City</label>
                <input
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                />
              </div>
            </div>
            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">State</label>
                <select
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={filters.state_id}
                  onChange={(e) => handleFilterStateChange(e.target.value)}
                >
                  <option value="">All</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.state}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">District</label>
                <select
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={filters.district_id}
                  disabled={!filters.state_id}
                  onChange={(e) => setFilters({ ...filters, district_id: e.target.value })}
                >
                  <option value="">All</option>
                  {filterDistricts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.district}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-row gap-2 h-10 mt-6">
              <button className="btn btn-outline-primary p-2" onClick={() => applyFilters(filters)}>
                Search
              </button>
              <button className="btn btn-outline-primary p-2" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <DataTableComponent
          keyField="id"
          columns={columns}
          data={cityList}
          totalRows={total}
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          paginationDefaultPage={currentPage}
        />

        <div className="fixed bottom-0 right-10 w-16 h-16 mr-10 mb-6 cursor-pointer" id="box_btn">
          <div className="text-right">
            <Menu className="relative inline-block text-left">
              <div>
                <Menu.Button
                  onClick={openAddModal}
                  className="inline-flex justify-cente w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                  style={{ backgroundColor: `${primaryColor["700"]}` }}
                >
                  <HiOutlinePlus className="w-full h-full p-3" />
                </Menu.Button>
              </div>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isBusy && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <CircleLoaderComponent />
        </div>
      )}

      {embedded ? (
        content
      ) : (
        <div className="flex w-full bg-gray-200 h-full justify-center">
          <div className="w-full">
            <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
              City List
              <LinksComponent />
            </div>
            {content}
          </div>
        </div>
      )}

      {showFormModal && (
        <ModalComponent show={showFormModal} setShow={closeFormModal} size="md">
          <div className="p-6 flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">{isEdit ? "Edit City" : "Add City"}</h2>

            {!isEdit && (
              <div className="flex justify-center gap-2 border-b">
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "manual"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("manual")}
                >
                  Add Manually
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "csv"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("csv")}
                >
                  Upload CSV
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "export"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("export")}
                >
                  Export CSV
                </button>
              </div>
            )}

            {isEdit || addTab === "manual" ? (
              <>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <select
                      className={inputClass}
                      value={form.state_id}
                      onChange={(e) => handleFormStateChange(e.target.value)}
                    >
                      <option value="">Select state</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.state}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">District</label>
                    <select
                      className={inputClass}
                      value={form.district_id}
                      disabled={!form.state_id || formDistrictsLoading}
                      onChange={(e) => setForm({ ...form, district_id: e.target.value })}
                    >
                      <option value="">Select district</option>
                      {formDistricts.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.district}
                        </option>
                      ))}
                    </select>
                    {formDistrictsLoading && <p className="mt-1 text-xs text-gray-500">Loading districts...</p>}
                    {!form.state_id && (
                      <p className="mt-1 text-xs text-gray-500">Select a state to choose a district (optional).</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">City Name *</label>
                    <input
                      type="text"
                      maxLength={CITY_NAME_MAX_LENGTH}
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      placeholder="e.g. Lucknow"
                      className={inputClass}
                    />
                  </div>

                  {isEdit && Number(form.location_count) > 0 && (
                    <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-md p-2">
                      This city is mapped to {form.location_count} zone location(s). A district change is only allowed if
                      it doesn't conflict with those mappings.
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !form.city.trim()}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      isSaving || !form.city.trim() ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isEdit ? "Update" : "Add"}
                  </button>
                </div>
              </>
            ) : addTab === "csv" ? (
              <div className="flex flex-col gap-3">
                <p className="text-xs text-gray-500">
                  CSV columns: <span className="font-semibold">state</span>,{" "}
                  <span className="font-semibold">district</span> and <span className="font-semibold">city</span>. The
                  state must already exist; the district and city are created automatically if they don't already exist
                  under that state.
                </p>

                <div className="flex justify-center">
                  <button
                    type="button"
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                    onClick={() => downloadSampleCSV(cityCsvTemplate)}
                  >
                    Sample CSV Download
                  </button>
                </div>

                <label
                  htmlFor="city-csv-dropzone"
                  className="flex flex-col items-center justify-center w-full h-20 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                >
                  <p className="text-sm text-gray-500 text-center">
                    <span className="font-semibold">Choose CSV file</span>
                    <div>Click to upload or drag and drop</div>
                  </p>
                  <input
                    id="city-csv-dropzone"
                    type="file"
                    className="hidden"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                  />
                </label>
                {csvFile && <p className="text-xs text-gray-500 text-center">{csvFile.name}</p>}

                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleCsvSubmit}
                    disabled={csvUploading || !csvFile}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      csvUploading || !csvFile ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {csvUploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-gray-600">Download every city currently in the system as a CSV file.</p>
                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleExportCsv}
                    disabled={exportLoading}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      exportLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {exportLoading ? "Exporting..." : "Download CSV"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalComponent>
      )}

      {deleteTarget && (
        <ModalComponent show={Boolean(deleteTarget)} setShow={() => setDeleteTarget(null)} size="md">
          <div className="p-6 flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Delete City</h2>
            <p className="text-sm text-gray-700">
              Are you sure you want to delete city <span className="font-semibold">{deleteTarget.city}</span>? This
              cannot be undone.
            </p>
            {Number(deleteTarget.location_count) > 0 && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-300 rounded-md p-2">
                This city is mapped to {deleteTarget.location_count} zone location(s) and cannot be deleted.
              </p>
            )}
            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm text-white rounded bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default CityMasterList;
